/* eslint-disable react/react-in-jsx-scope */
import {
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { UserMode } from '../context/UserModeProvider';
import {
  DEFAULT_PROJECT_MODE,
  EMPTY_PROJECT_CAPABILITIES,
  EMPTY_SYSTEM_CAPABILITIES,
  ProjectCapabilities,
  SystemCapabilities,
} from '../types/Authorization';
import { getBackendHost } from './backendHost';
import { clearAuthReauthentication, saveAuthReturnUrl } from './authReturnUrl';

const oidcAuthority = process.env.REACT_APP_OIDC_AUTHORITY || '';
const oidcClientId = process.env.REACT_APP_OIDC_CLIENT_ID || '';
const oidcRedirectUri = process.env.REACT_APP_OIDC_REDIRECT_URI || `${window.location.origin}/`;
const oidcPostLogoutRedirectUri = process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI || oidcRedirectUri;
const oidcScope = process.env.REACT_APP_OIDC_SCOPE || 'openid profile email';
const oidcAudience = process.env.REACT_APP_OIDC_AUDIENCE || '';
const oidcTokenEndpoint = `${getBackendHost()}/v1/auth/token`;

const userManagerSettings: UserManagerSettings = {
  authority: oidcAuthority || 'https://invalid.onelogin.local/oidc/2',
  client_id: oidcClientId || 'missing-client-id',
  redirect_uri: oidcRedirectUri,
  // OneLogin does not whitelist offline_access in this tenant, so renewal
  // uses the authorization endpoint through the same already-registered URI.
  // signinCallback() below distinguishes redirect and silent callbacks.
  silent_redirect_uri: oidcRedirectUri,
  post_logout_redirect_uri: oidcPostLogoutRedirectUri,
  response_type: 'code',
  scope: oidcScope,
  // Keep discovery and authorization in OneLogin, but exchange the code
  // through PHIA. This follows the working Certificados BFF pattern and
  // avoids a browser POST to OneLogin's token endpoint/CORS policy.
  metadataSeed: { token_endpoint: oidcTokenEndpoint },
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: false,
  ...(oidcAudience ? {
    extraQueryParams: { audience: oidcAudience },
    extraTokenParams: { audience: oidcAudience },
  } : {}),
};

const userManager = new UserManager(userManagerSettings);
let currentUser: User | null = null;

/** Navigate to an error page without reloading the bundle and restarting OIDC. */
export const navigateToAuthError = (path: '/error-permisos' | '/error-usuario') => {
  if (window.location.pathname === path) return;
  window.history.replaceState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export interface AuthContextValues {
  isAuthenticated: boolean;
  systemCapabilitiesLoaded: boolean;
  username: string;
  email: string;
  projectCapabilities: ProjectCapabilities;
  systemCapabilities: SystemCapabilities;
  logout: () => void;
  defaultMode: () => UserMode;
  can: (capability: keyof ProjectCapabilities) => boolean;
  canSystem: (capability: keyof SystemCapabilities) => boolean;
  setProjectCapabilities: (capabilities: ProjectCapabilities) => void;
  clearProjectCapabilities: () => void;
}

const AuthContextData: AuthContextValues = {
  isAuthenticated: false,
  systemCapabilitiesLoaded: false,
  username: '',
  email: '',
  projectCapabilities: EMPTY_PROJECT_CAPABILITIES,
  systemCapabilities: EMPTY_SYSTEM_CAPABILITIES,
  logout: () => undefined,
  defaultMode: () => 'viewer',
  can: () => false,
  canSystem: () => false,
  setProjectCapabilities: () => undefined,
  clearProjectCapabilities: () => undefined,
};

export const AuthContext = createContext<AuthContextValues>(AuthContextData);

interface AuthContextProviderProps {
  children: JSX.Element;
  /** Error pages must be renderable without restarting OIDC. */
  authenticate?: boolean;
  loadSystemCapabilities?: boolean;
}

const AuthContextProvider = ({
  children,
  authenticate = true,
  loadSystemCapabilities = true,
}: AuthContextProviderProps) => {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [projectCapabilities, setProjectCapabilitiesState] = useState<ProjectCapabilities>(EMPTY_PROJECT_CAPABILITIES);
  const [systemCapabilities, setSystemCapabilities] = useState<SystemCapabilities>(EMPTY_SYSTEM_CAPABILITIES);
  const [systemCapabilitiesLoaded, setSystemCapabilitiesLoaded] = useState(false);

  useEffect(() => {
    if (!authenticate) return;
    setAuthenticated(Boolean(currentUser && !currentUser.expired && getAccessToken()));
  }, [authenticate]);

  useEffect(() => {
    if (!authenticate || !isAuthenticated || !loadSystemCapabilities) return;

    const loadSession = async () => {
      const profile = (currentUser?.profile || {}) as Record<string, string | undefined>;
      const displayName = profile.given_name
        ? [profile.given_name, profile.family_name].filter(Boolean).join(' ')
        : profile.name || profile.preferred_username || profile.email || '';
      const profileEmail = profile.email || profile.preferred_username || '';
      setUsername(displayName);
      setEmail(profileEmail);

      try {
        const backend = getBackendHost();
        const response = await fetch(`${backend}/v1/permisos/system`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (!response.ok) {
          if (response.status === 401) {
            clearAuthStorage();
            navigateToAuthError('/error-permisos');
          } else if (response.status === 403) {
            navigateToAuthError('/error-usuario');
          } else {
            navigateToAuthError('/error-permisos');
          }
          return;
        }
        setSystemCapabilities(await response.json() as SystemCapabilities);
        clearAuthReauthentication();
        setSystemCapabilitiesLoaded(true);
      } catch (error) {
        console.error('Unable to load system capabilities', error);
        navigateToAuthError('/error-permisos');
      }
    };

    void loadSession();
  }, [authenticate, isAuthenticated, loadSystemCapabilities]);

  const logout = useCallback(() => {
    currentUser = null;
    clearLegacyAuthStorage();
    void userManager.signoutRedirect({
      post_logout_redirect_uri: oidcPostLogoutRedirectUri,
    });
  }, []);

  const can = useCallback(
    (capability: keyof ProjectCapabilities) => projectCapabilities[capability],
    [projectCapabilities],
  );
  const canSystem = useCallback(
    (capability: keyof SystemCapabilities) => systemCapabilities[capability],
    [systemCapabilities],
  );
  const setProjectCapabilities = useCallback((capabilities: ProjectCapabilities) => {
    setProjectCapabilitiesState(capabilities);
  }, []);
  const clearProjectCapabilities = useCallback(() => {
    setProjectCapabilitiesState(EMPTY_PROJECT_CAPABILITIES);
  }, []);
  const defaultMode = useCallback((): UserMode => DEFAULT_PROJECT_MODE, []);

  const value = useMemo<AuthContextValues>(() => ({
    isAuthenticated,
    systemCapabilitiesLoaded,
    username,
    email,
    projectCapabilities,
    systemCapabilities,
    logout,
    defaultMode,
    can,
    canSystem,
    setProjectCapabilities,
    clearProjectCapabilities,
  }), [
    isAuthenticated,
    systemCapabilitiesLoaded,
    username,
    email,
    projectCapabilities,
    systemCapabilities,
    logout,
    defaultMode,
    can,
    canSystem,
    setProjectCapabilities,
    clearProjectCapabilities,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const hasOidcCallback = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

const requireOidcConfiguration = (): void => {
  if (!oidcAuthority || !oidcClientId) {
    throw new Error('OneLogin OIDC authority and client ID are required');
  }
};

const initAuthContextProvider = async (): Promise<boolean> => {
  requireOidcConfiguration();

  if (hasOidcCallback()) {
    const callbackUser = await userManager.signinCallback();
    // A silent callback is consumed by oidc-client-ts inside the iframe and
    // returns no User. The parent window completes the renewal request.
    if (!callbackUser) return false;
    currentUser = callbackUser;
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`);
  } else {
    currentUser = await userManager.getUser();
  }

  if (!currentUser || currentUser.expired || !getAccessToken()) {
    saveAuthReturnUrl();
    await userManager.signinRedirect();
    return false;
  }

  return true;
};

const refreshToken = async (): Promise<string> => {
  requireOidcConfiguration();
  const refreshedUser = await userManager.signinSilent();
  if (!refreshedUser) throw new Error('OneLogin did not return an authenticated session');
  currentUser = refreshedUser;
  const accessToken = refreshedUser.access_token;
  if (!accessToken) throw new Error('OneLogin did not return an access token');
  return accessToken;
};

const getAccessToken = (): string => currentUser?.access_token || '';

const clearLegacyAuthStorage = (): void => {
  localStorage.removeItem('tokenPhia');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('username');
  localStorage.removeItem('email');
};

const clearAuthStorage = (): void => {
  currentUser = null;
  void userManager.removeUser();
  clearLegacyAuthStorage();
};

export {
  AuthContextProvider,
  initAuthContextProvider,
  refreshToken,
  getAccessToken,
  clearAuthStorage,
};
